/**
 * ProposalPage (U15) — the tokenized proposal experience:
 *
 *   view → (select alternates) → review & respond → ESIGN consent →
 *   sign (typed signature + intent checkbox) → confirmation + signed copy
 *   — or decline with a reason.
 *
 * The document is the frozen render model verbatim; consent language comes
 * from the document, is shown before the sign step unlocks, and the consent
 * endpoint records it server-side before accept is ever possible in this UI.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PublicAcceptRequestSchema, PublicDeclineRequestSchema } from '@buildledger/shared';
import type { PublicProposalResponse, RenderSection } from '@buildledger/shared';

import {
  ApiError,
  getProposal,
  postAccept,
  postConsent,
  postDecline,
  proposalPdfUrl,
  signedCopyUrl,
} from '../api';
import { DocumentView } from '../DocumentView';
import { fmtDate, fmtMoney } from '../format';
import { Markdown } from '../markdown';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; status: number; message: string }
  | { kind: 'ready'; data: PublicProposalResponse };

type Modal = 'consent' | 'sign' | 'decline' | null;

function consentStorageKey(token: string): string {
  return `bl-consent-${token.slice(0, 12)}`;
}

export function ProposalPage({ token }: { token: string }): JSX.Element {
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [modal, setModal] = useState<Modal>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [consentDone, setConsentDone] = useState<boolean>(
    () => sessionStorage.getItem(consentStorageKey(token)) === '1',
  );
  const [justResponded, setJustResponded] = useState<'accepted' | 'declined' | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await getProposal(token);
      setLoad({ kind: 'ready', data });
    } catch (err) {
      if (err instanceof ApiError) setLoad({ kind: 'error', status: err.status, message: err.message });
      else setLoad({ kind: 'error', status: 0, message: 'Something went wrong loading this proposal.' });
    }
  }, [token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const document_ = load.kind === 'ready' ? load.data.document : null;

  const toggleAlternate = useCallback(
    (section: RenderSection) => {
      if (document_ === null) return;
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(section.sectionId)) {
          next.delete(section.sectionId);
          return next;
        }
        // One per named group: picking an option drops its group siblings.
        if (section.alternateGroup !== null) {
          for (const alt of document_.pricing.alternates) {
            if (alt.alternateGroup === section.alternateGroup) next.delete(alt.sectionId);
          }
        }
        next.add(section.sectionId);
        return next;
      });
    },
    [document_],
  );

  if (load.kind === 'loading') {
    return (
      <div className="stage stage-center">
        <div className="spinner" aria-label="Loading proposal" />
      </div>
    );
  }

  if (load.kind === 'error') {
    return <ErrorScreen status={load.status} message={load.message} />;
  }

  const { document, state } = load.data;
  const responded = state.response !== null;
  const canRespond = state.canRespond && justResponded === null;

  return (
    <div className="stage">
      <TopBanner
        state={state}
        justResponded={justResponded}
        token={token}
      />

      <main className="doc-wrap">
        <DocumentView
          document={document}
          alternates={canRespond ? { selected, onToggle: toggleAlternate } : null}
        />
      </main>

      {canRespond ? (
        <div className="action-bar" role="toolbar" aria-label="Respond to this proposal">
          <div className="action-bar-inner">
            <div className="action-meta">
              <span className="action-total num">{fmtMoney(document.pricing.total)}</span>
              <span className="action-hint">
                {state.tokenExpiresAt === null
                  ? 'Review and respond below'
                  : `Link valid through ${fmtDate(state.tokenExpiresAt)}`}
              </span>
            </div>
            <div className="action-buttons">
              <a className="btn btn-ghost" href={proposalPdfUrl(token)} target="_blank" rel="noreferrer">
                Download PDF
              </a>
              <button className="btn btn-ghost btn-danger" onClick={() => setModal('decline')}>
                Decline
              </button>
              <button
                className="btn btn-primary"
                data-testid="accept-open"
                onClick={() => setModal(consentDone ? 'sign' : 'consent')}
              >
                Accept &amp; sign
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="action-bar action-bar-quiet">
          <div className="action-bar-inner">
            <div className="action-meta">
              <span className="action-hint">
                {responded || justResponded
                  ? 'This proposal has been responded to.'
                  : 'This proposal is view-only.'}
              </span>
            </div>
            <div className="action-buttons">
              <a className="btn btn-ghost" href={proposalPdfUrl(token)} target="_blank" rel="noreferrer">
                Download PDF
              </a>
              {(state.response?.kind === 'accepted' || justResponded === 'accepted') && (
                <a className="btn btn-primary" href={signedCopyUrl(token)} target="_blank" rel="noreferrer">
                  Signed copy (PDF)
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {modal === 'consent' && (
        <ConsentModal
          consentText={document.esign.consentText}
          token={token}
          onDone={() => {
            sessionStorage.setItem(consentStorageKey(token), '1');
            setConsentDone(true);
            setModal('sign');
          }}
          onClose={() => setModal(null)}
          onStateConflict={() => {
            setModal(null);
            void refresh();
          }}
        />
      )}
      {modal === 'sign' && (
        <SignModal
          token={token}
          alternates={[...selected]}
          alternateCount={selected.size}
          total={document.pricing.total}
          onSigned={() => {
            setModal(null);
            setJustResponded('accepted');
            void refresh();
          }}
          onClose={() => setModal(null)}
          onStateConflict={() => {
            setModal(null);
            void refresh();
          }}
        />
      )}
      {modal === 'decline' && (
        <DeclineModal
          token={token}
          onDeclined={() => {
            setModal(null);
            setJustResponded('declined');
            void refresh();
          }}
          onClose={() => setModal(null)}
          onStateConflict={() => {
            setModal(null);
            void refresh();
          }}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ banners

function TopBanner({
  state,
  justResponded,
  token,
}: {
  state: PublicProposalResponse['state'];
  justResponded: 'accepted' | 'declined' | null;
  token: string;
}): JSX.Element | null {
  const kind = justResponded ?? state.response?.kind ?? null;
  if (kind === 'accepted') {
    return (
      <div className="banner banner-ok" data-testid="banner-accepted">
        <strong>Accepted &amp; signed.</strong>{' '}
        {state.response?.signerName
          ? `Signed by ${state.response.signerName} on ${fmtDate(state.response.respondedAt)}. `
          : 'Thank you — your signature has been recorded. '}
        <a href={signedCopyUrl(token)} target="_blank" rel="noreferrer">
          Download your signed copy
        </a>{' '}
        — this link keeps working for your records.
      </div>
    );
  }
  if (kind === 'declined') {
    return (
      <div className="banner" data-testid="banner-declined">
        <strong>Declined.</strong> Your response was sent to the contractor. If anything changes,
        just reach out to them directly.
      </div>
    );
  }
  if (state.versionStatus === 'superseded') {
    return (
      <div className="banner banner-warn">
        <strong>A newer version of this proposal exists.</strong> Ask the contractor for the
        updated link before responding.
      </div>
    );
  }
  if (state.versionStatus === 'expired') {
    return (
      <div className="banner banner-warn">
        <strong>This proposal version has expired.</strong> Contact the contractor for a current
        one.
      </div>
    );
  }
  return null;
}

function ErrorScreen({ status, message }: { status: number; message: string }): JSX.Element {
  const title =
    status === 404
      ? 'This proposal link is not valid'
      : status === 410
        ? 'This proposal link has expired'
        : status === 429
          ? 'Slow down a moment'
          : 'Something went wrong';
  const hint =
    status === 404
      ? 'Double-check the link from your email — it may have been revoked or replaced.'
      : status === 410
        ? 'Ask the contractor to send a fresh link.'
        : status === 429
          ? 'Too many requests from your connection. Wait a minute and reload.'
          : message;
  return (
    <div className="stage stage-center">
      <div className="card notice-card">
        <div className="brand-rule" />
        <h1>{title}</h1>
        <p>{hint}</p>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------- modal

function ModalShell({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="overlay" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="modal-x" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function useSubmit(onStateConflict: () => void): {
  busy: boolean;
  error: string | null;
  run: (fn: () => Promise<void>) => void;
} {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(
    (fn: () => Promise<void>) => {
      setBusy(true);
      setError(null);
      void fn()
        .catch((err: unknown) => {
          if (err instanceof ApiError && (err.status === 409 || err.status === 410)) {
            onStateConflict();
            return;
          }
          setError(err instanceof ApiError ? err.message : 'Something went wrong — try again.');
        })
        .finally(() => setBusy(false));
    },
    [onStateConflict],
  );
  return { busy, error, run };
}

// ------------------------------------------------------------------ consent

function ConsentModal({
  consentText,
  token,
  onDone,
  onClose,
  onStateConflict,
}: {
  consentText: string;
  token: string;
  onDone: () => void;
  onClose: () => void;
  onStateConflict: () => void;
}): JSX.Element {
  const [agree, setAgree] = useState(false);
  const { busy, error, run } = useSubmit(onStateConflict);
  return (
    <ModalShell title="Electronic records & signatures" onClose={onClose}>
      <div className="modal-body">
        <Markdown className="consent-text" text={consentText} />
        <label className="check-row">
          <input
            type="checkbox"
            checked={agree}
            data-testid="consent-agree"
            onChange={(e) => setAgree(e.target.checked)}
          />
          <span>I agree to use electronic records and signatures for this proposal.</span>
        </label>
        {error && <p className="form-error">{error}</p>}
      </div>
      <div className="modal-foot">
        <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          disabled={!agree || busy}
          data-testid="consent-continue"
          onClick={() =>
            run(async () => {
              await postConsent(token);
              onDone();
            })
          }
        >
          {busy ? 'Saving…' : 'Agree & continue'}
        </button>
      </div>
    </ModalShell>
  );
}

// --------------------------------------------------------------------- sign

function SignModal({
  token,
  alternates,
  alternateCount,
  total,
  onSigned,
  onClose,
  onStateConflict,
}: {
  token: string;
  alternates: string[];
  alternateCount: number;
  total: string;
  onSigned: () => void;
  onClose: () => void;
  onStateConflict: () => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [signature, setSignature] = useState('');
  const [intent, setIntent] = useState(false);
  const { busy, error, run } = useSubmit(onStateConflict);

  const parsed = useMemo(
    () =>
      PublicAcceptRequestSchema.safeParse({
        name: name.trim(),
        email: email.trim(),
        typedSignature: signature.trim(),
        alternates,
        intentConfirmed: intent || undefined,
      }),
    [name, email, signature, alternates, intent],
  );

  return (
    <ModalShell title="Sign & accept this proposal" onClose={onClose}>
      <div className="modal-body">
        <p className="sign-summary">
          You are accepting the proposal at its stated total of{' '}
          <strong className="num">{fmtMoney(total)}</strong>
          {alternateCount > 0
            ? `, plus ${alternateCount} selected alternate${alternateCount === 1 ? '' : 's'}`
            : ''}
          . Your name, email, and the time of signing are recorded with the document.
        </p>
        <label className="field">
          <span>Your full name</span>
          <input
            value={name}
            data-testid="sign-name"
            autoComplete="name"
            onChange={(e) => setName(e.target.value)}
            placeholder="Jordan Smith"
          />
        </label>
        <label className="field">
          <span>Email</span>
          <input
            value={email}
            data-testid="sign-email"
            type="email"
            autoComplete="email"
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </label>
        <label className="field">
          <span>Type your name to sign</span>
          <input
            value={signature}
            data-testid="sign-signature"
            className="signature-input"
            onChange={(e) => setSignature(e.target.value)}
            placeholder={name.trim() === '' ? 'Your signature' : name.trim()}
          />
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={intent}
            data-testid="sign-intent"
            onChange={(e) => setIntent(e.target.checked)}
          />
          <span>
            I intend this typed name to be my electronic signature on this proposal, and I agree to
            its terms.
          </span>
        </label>
        {error && <p className="form-error">{error}</p>}
      </div>
      <div className="modal-foot">
        <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
          Back
        </button>
        <button
          className="btn btn-primary"
          disabled={!parsed.success || busy}
          data-testid="sign-submit"
          onClick={() =>
            run(async () => {
              if (!parsed.success) return;
              await postAccept(token, parsed.data);
              onSigned();
            })
          }
        >
          {busy ? 'Signing…' : 'Sign & accept'}
        </button>
      </div>
    </ModalShell>
  );
}

// ------------------------------------------------------------------ decline

function DeclineModal({
  token,
  onDeclined,
  onClose,
  onStateConflict,
}: {
  token: string;
  onDeclined: () => void;
  onClose: () => void;
  onStateConflict: () => void;
}): JSX.Element {
  const [reason, setReason] = useState('');
  const [name, setName] = useState('');
  const { busy, error, run } = useSubmit(onStateConflict);

  const parsed = useMemo(
    () =>
      PublicDeclineRequestSchema.safeParse({
        reason: reason.trim(),
        ...(name.trim() === '' ? {} : { name: name.trim() }),
      }),
    [reason, name],
  );

  return (
    <ModalShell title="Decline this proposal" onClose={onClose}>
      <div className="modal-body">
        <p className="sign-summary">
          Let the contractor know why — it goes straight to them and helps them follow up well.
        </p>
        <label className="field">
          <span>Reason</span>
          <textarea
            value={reason}
            data-testid="decline-reason"
            rows={4}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. We went with another bid / the timing changed / the scope needs to change…"
          />
        </label>
        <label className="field">
          <span>Your name (optional)</span>
          <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
        </label>
        {error && <p className="form-error">{error}</p>}
      </div>
      <div className="modal-foot">
        <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
          Back
        </button>
        <button
          className="btn btn-danger-solid"
          disabled={!parsed.success || busy}
          data-testid="decline-submit"
          onClick={() =>
            run(async () => {
              if (!parsed.success) return;
              await postDecline(token, parsed.data);
              onDeclined();
            })
          }
        >
          {busy ? 'Sending…' : 'Decline proposal'}
        </button>
      </div>
    </ModalShell>
  );
}
